import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';
import { BILLING_TEXT_PLACEHOLDERS, billingTextsApi, type BillingTextRow } from '@/api/billingTexts';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';

type RowDraft = {
  key: string;
  lang: string;
  text: string;
};

function rowId(row: Pick<BillingTextRow, 'key' | 'lang'>): string {
  return `${row.key}::${row.lang}`;
}

function emptyDraft(): RowDraft {
  return { key: '', lang: 'he', text: '' };
}

export default function BillingTextsSection() {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newRow, setNewRow] = useState<RowDraft>(emptyDraft());
  const [modalError, setModalError] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['billing-texts'],
    queryFn: billingTextsApi.list,
    staleTime: 15_000,
  });

  const getDraft = (row: BillingTextRow): RowDraft => {
    const id = rowId(row);
    if (drafts[id]) return drafts[id];
    return { key: row.key, lang: row.lang, text: row.text };
  };

  const updateDraft = (row: BillingTextRow, patch: Partial<RowDraft>) => {
    const id = rowId(row);
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...getDraft(row), ...patch },
    }));
  };

  const saveMutation = useMutation({
    mutationFn: billingTextsApi.upsert,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['billing-texts'] });
      const id = rowId(variables);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSavingId(null);
    },
    onError: () => {
      setSavingId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: billingTextsApi.upsert,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing-texts'] });
      setModalOpen(false);
      setNewRow(emptyDraft());
      setModalError('');
    },
    onError: (err) => {
      setModalError((err as Error).message);
    },
  });

  const handleSave = (row: BillingTextRow) => {
    const draft = getDraft(row);
    setSavingId(rowId(row));
    saveMutation.mutate(draft);
  };

  const isDirty = (row: BillingTextRow): boolean => {
    const draft = getDraft(row);
    return draft.text !== row.text;
  };

  return (
    <>
      <section className="card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-indigo-500/10 p-2 text-indigo-400">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink-primary">Billing texts</h2>
              <p className="mt-1 text-sm text-ink-secondary">
                Approval and consent copy shown in JumpStart Hub when customers buy add-ons or upgrade plans.
                Placeholders: {BILLING_TEXT_PLACEHOLDERS.join(', ')}.
              </p>
            </div>
          </div>
          <Button type="button" onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Add row
          </Button>
        </div>

        {isLoading ? (
          <Loading text="Loading billing texts..." />
        ) : rows.length === 0 ? (
          <EmptyState title="No billing texts" description="Seed rows exist on dgxn — check platform admin access." />
        ) : (
          <div className="space-y-4">
            {rows.map((row) => {
              const draft = getDraft(row);
              const dirty = isDirty(row);
              const id = rowId(row);
              return (
                <div key={id} className="rounded-xl border border-navy-border bg-navy-card/40 p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <code className="text-sm text-gold">{row.key}</code>
                    <span className="rounded-full bg-navy-border px-2 py-0.5 text-xs text-ink-secondary">
                      {row.lang}
                    </span>
                    {row.updated_at ? (
                      <span className="text-xs text-ink-secondary">
                        Updated {new Date(row.updated_at).toLocaleString()}
                      </span>
                    ) : null}
                  </div>
                  <textarea
                    className="w-full min-h-[120px] rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm text-ink-primary"
                    value={draft.text}
                    onChange={(e) => updateDraft(row, { text: e.target.value })}
                    dir={draft.lang === 'he' ? 'rtl' : 'ltr'}
                  />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      disabled={!dirty || savingId === id}
                      onClick={() => handleSave(row)}
                    >
                      {savingId === id ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add billing text">
        <div className="space-y-4">
          <Input
            label="Key"
            value={newRow.key}
            onChange={(e) => setNewRow((prev) => ({ ...prev, key: e.target.value }))}
            placeholder="approval_statement"
          />
          <Input
            label="Language"
            value={newRow.lang}
            onChange={(e) => setNewRow((prev) => ({ ...prev, lang: e.target.value }))}
            placeholder="he"
          />
          <div>
            <label className="mb-1 block text-sm text-ink-secondary">Text</label>
            <textarea
              className="w-full min-h-[120px] rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm text-ink-primary"
              value={newRow.text}
              onChange={(e) => setNewRow((prev) => ({ ...prev, text: e.target.value }))}
              dir={newRow.lang === 'he' ? 'rtl' : 'ltr'}
            />
          </div>
          {modalError ? <p className="text-sm text-red-400">{modalError}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!newRow.key.trim() || !newRow.text.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate(newRow)}
            >
              {createMutation.isPending ? 'Saving…' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
